"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, ShieldCheck, Send, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface OperatingMetric {
  label: string;
  value: string;
  status: string;
}
const operatingMetrics: OperatingMetric[] = [{
  label: "DISPATCH DESK",
  value: "MON - FRI // 07:00 - 18:00 PST",
  status: "ACTIVE"
}, {
  label: "TECHNICAL RESPONSE",
  value: "< 2 HOURS AVERAGE TURNAROUND",
  status: "OPTIMAL"
}, {
  label: "WAREHOUSE INVENTORY",
  value: "ORANGE FACILITY, CALIFORNIA",
  status: "STANDBY"
}];
const inquiryOptions = [{
  value: "general_inquiry",
  label: "General Technical Inquiry"
}, {
  value: "order_status",
  label: "Order Status / Tracking Reference"
}, {
  value: "bulk_procurement",
  label: "Commercial / Bulk Workshop Order"
}, {
  value: "returns_warranty",
  label: "Tool Warranty & Service RMA"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card border border-primary/30 rounded-xs">
        <div className="p-3 mb-4 rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground">
          Transmission Received
        </h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">
          {successMessage || "Your request has been logged into our technical dispatch queue. A tool specialist will respond shortly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 uppercase font-serif tracking-wider font-semibold border-border hover:border-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters required"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Name</span>
                <span className="text-primary font-mono text-[10px]">REQUIRED</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Your name" className="rounded-xs bg-input border-border focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 h-11" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                  <span>Email</span>
                  <span className="text-primary font-mono text-[10px]">REQUIRED</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" className="rounded-xs bg-input border-border focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 h-11" {...field} />
                </FormControl>
                <FormMessage className="text-xs text-destructive" />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                  <span>Phone</span>
                  <span className="text-muted-foreground/50 font-mono text-[10px]">OPTIONAL</span>
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="Your phone number" className="rounded-xs bg-input border-border focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 h-11" {...field} />
                </FormControl>
                <FormMessage className="text-xs text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="order_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Order Number</span>
                <span className="text-muted-foreground/50 font-mono text-[10px]">SELECT</span>
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full rounded-xs bg-input border-border focus-visible:border-primary text-foreground h-11">
                    <SelectValue placeholder="Select order (if applicable)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  {inquiryOptions.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i} className="text-sm focus:bg-accent focus:text-accent-foreground cursor-pointer">
                      {opt.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Please provide at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Message</span>
                <span className="text-primary font-mono text-[10px]">REQUIRED</span>
              </FormLabel>
              <FormControl>
                <Textarea placeholder="How can we help?" className="rounded-xs bg-input border-border focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 min-h-28" {...field} />
              </FormControl>
              <FormMessage className="text-xs text-destructive" />
            </FormItem>} />

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-xs flex items-center gap-2 text-destructive-foreground text-xs">
            <AlertCircle className="size-4 shrink-0 text-destructive" />
            <p>{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xs font-serif font-bold uppercase tracking-wider text-base transition-colors">
          {isSubmitting ? <span className="flex items-center gap-2">
              <span className="size-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              LOGGING TRANSMISSION...
            </span> : <span className="flex items-center justify-center gap-2">
              <Send className="size-4" />
              SUBMIT TRANSMISSION
            </span>}
        </Button>
      </div>
    </Form>;
}
export default function ContactDetails() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.24,
        ease: "easeOut"
      }
    }
  };
  return <section id="contact-details" data-nav="dark" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={itemVariants} className="mb-12 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-primary font-bold">
              DIRECT DESK & LOGISTICS
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground flex items-center gap-3">
            <span className="w-1.5 h-8 md:h-10 bg-primary inline-block shrink-0" />
            Contact & Details
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans">
            Reach our equipment specialists for tool specifications, bulk workshop quotes, or order fulfillment tracking across the United States.
          </p>
        </motion.div>

        {/* Two-Column Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: Direct Technical Contact Form */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <Card className="bg-card border-border rounded-xs shadow-md p-6 sm:p-8">
              <CardHeader className="p-0 mb-6 pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                      COMMUNICATION PROTOCOL
                    </span>
                    <CardTitle className="font-serif text-2xl font-bold uppercase tracking-tight text-card-foreground mt-1">
                      Direct Dispatch Form
                    </CardTitle>
                  </div>
                  <Badge variant="outline" className="font-mono text-[10px] uppercase border-border text-muted-foreground px-2 py-1">
                    SSL SECURED
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <FormProvider formId="contact_form" sectionName="contact-details" defaultValues={{
                name: "",
                email: "",
                phone: "",
                order_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="SUBMIT TRANSMISSION" successMessage="Thank you. Your message has been routed to our technical support team." errorMessage="Failed to dispatch transmission. Please verify required fields and try again.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Company Credentials & Operational Location */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-6">
            {/* Legal Entity & Direct Lines Card */}
            <Card className="bg-card border-border rounded-xs shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="size-5 text-primary" />
                <h3 className="font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                  Verified Headquarters
                </h3>
              </div>

              <div className="space-y-4 text-sm font-sans">
                <div className="p-3 bg-muted/40 border border-border rounded-xs">
                  <span className="font-mono text-[11px] uppercase text-muted-foreground block">
                    REGISTERED LEGAL ENTITY
                  </span>
                  <span className="font-semibold text-foreground text-base block mt-0.5">
                    Huga Scalet LLC
                  </span>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <MapPin className="size-4 text-primary shrink-0 mt-1" />
                  <div>
                    <span className="font-mono text-[11px] uppercase text-muted-foreground block">
                      PHYSICAL ADDRESS
                    </span>
                    <span className="text-foreground leading-relaxed block mt-0.5">
                      2000 W Palmyra Ave, Ste 28, Orange, CA 92868, USA
                    </span>
                  </div>
                </div>

                <Separator className="bg-border" />

                <div className="flex items-start gap-3">
                  <Phone className="size-4 text-primary shrink-0 mt-1" />
                  <div>
                    <span className="font-mono text-[11px] uppercase text-muted-foreground block">
                      DIRECT PHONE LINE
                    </span>
                    <a href="tel:+14842918653" className="text-foreground hover:text-primary transition-colors font-mono font-medium block mt-0.5">
                      +1 (484) 291-8653
                    </a>
                  </div>
                </div>

                <Separator className="bg-border" />

                <div className="flex items-start gap-3">
                  <Mail className="size-4 text-primary shrink-0 mt-1" />
                  <div>
                    <span className="font-mono text-[11px] uppercase text-muted-foreground block">
                      SECURE EMAIL DISPATCH
                    </span>
                    <a href="mailto:HugaScalet@proton.me" className="text-foreground hover:text-primary transition-colors font-mono font-medium block mt-0.5">
                      HugaScalet@proton.me
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Operating Specs Readout */}
            <Card className="bg-card border-border rounded-xs shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="size-5 text-primary" />
                <h3 className="font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                  Operating Specifications
                </h3>
              </div>

              <div className="space-y-3">
                {operatingMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="p-3 bg-muted/30 border border-border rounded-xs flex flex-col justify-between gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wider">
                        {metric.label}
                      </span>
                      <span className="font-mono text-[10px] text-primary font-semibold">
                        {metric.status}
                      </span>
                    </div>
                    <span className="font-mono text-xs text-foreground font-medium">
                      {metric.value}
                    </span>
                  </div>)}
              </div>
            </Card>

            {/* Embedded Location Map Preview */}
            <Card className="bg-card border-border rounded-xs shadow-md overflow-hidden p-1">
              <div className="relative w-full h-44 bg-muted border border-border rounded-xs overflow-hidden flex flex-col justify-between p-4">
                <div className="flex items-center justify-between z-10">
                  <span className="font-mono text-[11px] uppercase tracking-wider bg-background/90 text-foreground px-2 py-0.5 border border-border rounded-xs font-semibold">
                    ORANGE, CA FACILITY
                  </span>
                  <Badge variant="outline" className="bg-background/90 border-primary/50 text-primary font-mono text-[10px]">
                    33.7877° N, 117.8678° W
                  </Badge>
                </div>

                <div className="absolute inset-0 opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_center,_var(--color-primary)_0%,_transparent_70%)]" />

                <div className="z-10 mt-auto flex items-center justify-between bg-background/95 p-2.5 border border-border rounded-xs">
                  <div className="flex items-center gap-2">
                    <MapPin className="size-4 text-primary shrink-0" />
                    <span className="font-mono text-xs text-foreground truncate max-w-[200px] sm:max-w-xs">
                      2000 W Palmyra Ave, Ste 28
                    </span>
                  </div>
                  <Link href="https://maps.google.com/?q=2000+W+Palmyra+Ave+Ste+28+Orange+CA+92868" target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-primary hover:underline flex items-center gap-1 font-semibold">
                    MAP
                  </Link>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}